import { Box } from '@mui/material';
import { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGoalDetailsQuery, useUsersListQuery } from 'api';
import { GoBackButtonWithTitle, PageWrapper, Backdrop, NewConfirmDialog } from 'components';
import { RefetchGoalDetails } from 'features/career/types';
import { CommentsList } from 'features/comments';
import { useAuth } from 'hooks';

import { AddGoalCommentForm, GoalForm } from '../../components';
import { useDeleteGoal } from './hooks';
import { CommentsW, ContentW, FormW, PageHeaderW } from './styled';

export function EditGoalPage(): ReactElement {
  const { t } = useTranslation();
  const commentsSectionRef = useRef<HTMLDivElement>(null);
  const { achievementId = '', employeeId: userId = '', goalId = '' } = useParams();
  const { user: currentUser, isManager } = useAuth();
  const currentUserId = currentUser?.id ?? '';

  const {
    data,
    isLoading: isDetailsLoading,
    isSuccess: isDetailsLoaded,
    refetch: refetchGoal,
  } = useGoalDetailsQuery({
    userId,
    achievementId,
    goalId,
  });

  const { isLoading: isLoadingUsersList, usersList } = useUsersListQuery({});

  const {
    isConfirmDeleteLoading,
    isDeleteModalOpen,
    handleCloseDeleteModal,
    handleDeleteGoal,
    handleRemoveClick,
  } = useDeleteGoal();

  const goalOwnerId = data?.owner_id ?? '';
  const isRemoveAvailable =
    (userId === goalOwnerId && currentUserId === goalOwnerId) ||
    (isManager && currentUserId !== goalOwnerId && currentUserId !== userId);

  return (
    <>
      <PageWrapper>
        <PageHeaderW>
          <GoBackButtonWithTitle title={t('common.back')} />
        </PageHeaderW>
        <>
          <ContentW>
            <FormW>
              {(isDetailsLoading || isLoadingUsersList) && <Backdrop />}
              {data && isDetailsLoaded && (
                <GoalForm
                  isEdit={true}
                  data={data}
                  usersList={usersList}
                  isDetailsLoaded={isDetailsLoaded}
                  handleRemoveClick={isRemoveAvailable ? handleRemoveClick : undefined}
                  refetchGoal={refetchGoal as unknown as RefetchGoalDetails}
                />
              )}
            </FormW>
          </ContentW>
          {isDetailsLoaded && data?.comments?.length ? (
            <CommentsW ref={commentsSectionRef}>
              <Box sx={{ marginTop: '32px' }}>
                <AddGoalCommentForm userId={userId} achievementId={achievementId} goalId={goalId} />
              </Box>
              <CommentsList
                comments={data?.comments ?? []}
                postAuthor={data?.owner_id ?? ''}
                isLoading={false}
                isFetchingNextPage={false}
                lastItemRef={undefined}
              />
            </CommentsW>
          ) : (
            <></>
          )}
        </>
      </PageWrapper>

      <NewConfirmDialog
        title={t('career.goals.remove.title')}
        subTitle={t('career.goals.remove.subtitle')}
        open={isDeleteModalOpen}
        onConfirm={handleDeleteGoal}
        onClose={handleCloseDeleteModal}
        isLoading={isConfirmDeleteLoading}
        submitLabel={t('career.goals.remove.successButton')}
        cancelLabel={t('career.goals.remove.cancelButton')}
      />
    </>
  );
}
